import { useLayoutEffect, useRef, useState } from 'react'
import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import type { Currency } from '@shared/contracts/enums'
import { getUserFacingError } from '@shared/lib/errors/user-facing-error'
import {
  Banner,
  Button,
  CurrencyField,
  Drawer,
  DrawerSection,
  PercentageField,
  SegmentedControl,
  SelectField,
  TextField,
} from '@shared/design-system/ui'
import {
  useUpdateAdminReceivingPlan,
  useUpdateDefaultReceivingPlan,
} from '../api/admin-receiving-plan'
import { planChanges, planValidationIssues, type PlanValidationIssue } from '../model/plan-edit'
import {
  planName,
  planPercent,
  type ReceivingPlanFee,
  type ReceivingPlansResponse,
  type UserReceivingPlan,
} from '../model/user-receiving-plan'
import { PlanConditions } from './PlanConditions'
import * as styles from './receiving-plan.css'

export function AdminReceivingPlanEditor({
  userId,
  userLabel,
  data,
  onClose,
  scope = 'user',
}: {
  userId: string
  userLabel: string
  data: ReceivingPlansResponse
  onClose: () => void
  scope?: 'user' | 'default'
}) {
  const customFees = useFeatureFlag('customServiceFees') && scope === 'user'
  const [initial] = useState<UserReceivingPlan>(
    scope === 'default' ? { ...data.current, kind: 'CUSTOM' } : data.current,
  )
  const [draft, setDraft] = useState<UserReceivingPlan>(
    customFees ? initial : { ...initial, kind: 'CUSTOM' },
  )
  const customDraft = useRef<UserReceivingPlan>({ ...initial, kind: 'CUSTOM' })
  const standardPlan =
    data.standards.find((plan) => plan.receivingPeriod === initial.receivingPeriod) ??
    data.standards[0]
  const standardServiceFee = standardPlan?.serviceFee ?? { percentage: '9.9', fixedUsd: '1.49' }
  const [reason, setReason] = useState('')
  const withdrawalCurrency: Currency = 'BRL'
  const [phase, setPhase] = useState<'edit' | 'review'>('edit')
  const contentStart = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    contentStart.current?.scrollIntoView?.({ block: 'start' })
  }, [phase])
  const [errors, setErrors] = useState<string[]>([])
  const [fieldIssues, setFieldIssues] = useState<PlanValidationIssue[]>([])
  useLayoutEffect(() => {
    const first = fieldIssues[0]
    if (phase === 'edit' && first)
      contentStart.current
        ?.querySelector<HTMLInputElement>(
          `[data-validation-field="${first.currency}.${first.field}"]`,
        )
        ?.focus()
  }, [fieldIssues, withdrawalCurrency, phase])
  const userMutation = useUpdateAdminReceivingPlan(userId)
  const defaultMutation = useUpdateDefaultReceivingPlan()
  const mutation = scope === 'default' ? defaultMutation : userMutation
  const national = draft.fees.find((fee) => fee.currency === 'BRL')!
  const changes = planChanges(initial, draft)
  const fieldError = (currency: string, field: string) =>
    fieldIssues.find((issue) => issue.currency === currency && issue.field === field)?.message
  const fieldKey = (currency: string, field: string) => `${currency}.${field}`
  const patchFee = (patch: Partial<ReceivingPlanFee>) =>
    setDraft((value) => ({
      ...value,
      fees: value.fees.map((fee) => ({ ...fee, ...patch })),
    }))
  const setMode = (mode: string) => {
    if (mode === 'CUSTOM') {
      if (!customFees) setDraft((value) => ({ ...value, kind: 'CUSTOM' }))
      else if (draft.kind !== 'CUSTOM') setDraft(customDraft.current)
    } else {
      const standard = data.standards.find((plan) => plan.receivingPeriod === mode)
      if (standard) {
        if (customFees && draft.kind === 'CUSTOM') customDraft.current = draft
        setDraft(standard)
      }
    }
    setErrors([])
    setFieldIssues([])
  }
  const review = () => {
    const issues = planValidationIssues(draft, customFees)
    if (reason.trim().length < 3)
      issues.push({
        currency: '',
        field: 'reason',
        message: 'Informe o motivo da alteração com pelo menos 3 caracteres.',
      })
    const validation = [...new Set(issues.map((issue) => issue.message))]
    if (changes.length === 0) validation.push('Altere uma condição antes de continuar.')
    setFieldIssues(issues)
    setErrors(validation)
    if (validation.length === 0) setPhase('review')
  }
  const days = (
    label: string,
    value: number,
    onChange: (value: number) => void,
    currency: string,
    field: string,
  ) => (
    <TextField
      data-validation-field={fieldKey(currency, field)}
      label={label}
      type="number"
      min={currency === 'BRL' && field === 'nationalReserveDays' ? 3 : 0}
      max={currency === 'BRL' && field === 'nationalReserveDays' ? 180 : 3650}
      step={1}
      value={Number.isNaN(value) ? '' : value}
      error={fieldError(currency, field)}
      data-testid={`receiving-plan-${field}-input`}
      helperText={
        currency === 'BRL' && field === 'nationalReserveDays'
          ? 'Informe de 3 a 180 dias. A mudança depende da confirmação da Stripe.'
          : undefined
      }
      onChange={(event) => onChange(event.target.value === '' ? NaN : Number(event.target.value))}
    />
  )
  return (
    <Drawer
      open
      data-testid="receiving-plan-editor"
      onClose={onClose}
      title={
        phase === 'edit'
          ? scope === 'default'
            ? 'Editar plano padrão'
            : 'Editar plano e taxas'
          : 'Revisar alteração do plano'
      }
      description={userLabel}
      size="wide"
      variant="form"
      busy={mutation.isPending}
      dirty={changes.length > 0 || reason.length > 0}
      footer={
        <div className={styles.actions}>
          <Button
            variant="secondary"
            disabled={mutation.isPending}
            onClick={phase === 'review' ? () => setPhase('edit') : onClose}
          >
            {phase === 'review' ? 'Voltar e editar' : 'Cancelar'}
          </Button>
          <Button
            data-testid="receiving-plan-review-save-btn"
            disabled={mutation.isPending || changes.length === 0}
            onClick={
              phase === 'edit'
                ? review
                : () =>
                    mutation.mutate(
                      {
                        plan: customFees
                          ? draft
                          : (({ serviceFee: _serviceFee, ...plan }) => plan)(draft),
                        reason: reason.trim(),
                        expectedVersionId: initial.versionId,
                        expectedDefaultVersionId: initial.defaultVersionId ?? null,
                      },
                      { onSuccess: onClose },
                    )
            }
          >
            {mutation.isPending
              ? 'Salvando…'
              : phase === 'edit'
                ? 'Revisar alterações'
                : 'Confirmar alteração'}
          </Button>
        </div>
      }
    >
      <div ref={contentStart} className={styles.stack}>
        {mutation.isError ? (
          <Banner tone="danger" title={getUserFacingError(mutation.error).title}>
            {getUserFacingError(mutation.error).description} Feche e abra o editor para conferir as
            condições atuais.
          </Banner>
        ) : null}
        {errors.length ? (
          <Banner tone="danger" title="Revise os campos">
            {errors.join(' ')}
          </Banner>
        ) : null}
        <Banner tone="info" title="Validade das novas condições">
          {customFees
            ? 'Assinaturas existentes mantêm as taxas e a reserva contratadas em todas as renovações. '
            : null}
          {customFees
            ? 'A taxa de serviço vale para novas vendas e novas assinaturas. Assinaturas existentes mantêm a taxa contratada. '
            : null}
          {scope === 'default'
            ? 'O novo padrão vale nos campos não personalizados. A reserva muda após a confirmação da Stripe para cada usuário. Vendas anteriores permanecem iguais. As taxas configuradas do plano são preservadas para histórico e configuração.'
            : 'A reserva muda após a confirmação da Stripe. Até lá, permanece a última reserva aplicada. Cada participante do split segue seu próprio plano. Recebíveis já gerados permanecem iguais.'}
        </Banner>
        {phase === 'review' ? (
          <>
            <h3 className={styles.heading}>
              {scope === 'default' ? 'Plano padrão' : `${planName(initial)} → ${planName(draft)}`}
            </h3>
            <div role="table" aria-label="Resumo das alterações">
              <div className={styles.changeRow} role="row">
                <strong role="columnheader">Condição</strong>
                <strong role="columnheader">Atual</strong>
                <strong role="columnheader">Após salvar</strong>
              </div>
              {changes.map((change) => (
                <div className={styles.changeRow} role="row" key={change.label}>
                  <span role="cell">{change.label}</span>
                  <span role="cell">{change.before}</span>
                  <strong role="cell">{change.after}</strong>
                </div>
              ))}
            </div>
            <DrawerSection title="Motivo da alteração">
              <p className={styles.copy}>{reason}</p>
            </DrawerSection>
          </>
        ) : (
          <>
            {customFees ? (
              <DrawerSection
                title="Condições do plano"
                description="Escolha se este usuário segue o plano padrão ou tem condições próprias."
              >
                <div className={styles.stack} data-testid="receiving-plan-mode-control">
                  <SegmentedControl
                    ariaLabel="Condições do plano"
                    value={draft.kind}
                    options={[
                      { label: 'Personalizar', value: 'CUSTOM' },
                      ...(standardPlan ? [{ label: 'Usar plano padrão', value: 'STANDARD' }] : []),
                    ]}
                    onChange={(mode) =>
                      setMode(mode === 'CUSTOM' ? mode : standardPlan!.receivingPeriod)
                    }
                  />
                  <p className={styles.copy}>
                    {draft.kind === 'CUSTOM'
                      ? 'A taxa de serviço e a reserva podem ser ajustadas separadamente.'
                      : 'Ao confirmar, a taxa de serviço, a reserva e as demais condições passam a seguir o plano padrão. Para mudar somente a taxa, use Personalizar.'}
                  </p>
                </div>
              </DrawerSection>
            ) : scope === 'user' ? (
              <div className={styles.fields}>
                <SelectField
                  label="Plano do usuário"
                  value={draft.kind === 'CUSTOM' ? 'CUSTOM' : draft.receivingPeriod}
                  options={[
                    { label: 'Personalizado', value: 'CUSTOM' },
                    ...data.standards.map((plan) => ({
                      label: 'Padrão',
                      value: plan.receivingPeriod,
                    })),
                  ]}
                  onChange={(event) => setMode(event.target.value)}
                />
                {draft.kind === 'CUSTOM' ? (
                  <SelectField
                    label="Copiar condições de um plano"
                    value=""
                    options={[
                      { label: 'Selecione para preencher', value: '' },
                      { label: 'Condições atuais', value: 'CURRENT' },
                      ...data.standards.map((plan) => ({
                        label: 'Padrão',
                        value: plan.receivingPeriod,
                      })),
                    ]}
                    onChange={(event) => {
                      const source =
                        event.target.value === 'CURRENT'
                          ? initial
                          : data.standards.find(
                              (plan) => plan.receivingPeriod === event.target.value,
                            )
                      if (source) setDraft({ ...source, kind: 'CUSTOM' })
                    }}
                  />
                ) : null}
              </div>
            ) : null}
            {draft.kind === 'CUSTOM' ? (
              <>
                {customFees ? (
                  <DrawerSection
                    title="Serviço VendePay"
                    description="Uma tarifa por cobrança, incluindo principal e order bumps. A parcela fixa em USD é convertida em reais na cobrança."
                  >
                    <div className={styles.fields} data-testid="receiving-plan-service-fee-fields">
                      <PercentageField
                        preserveInput={customFees}
                        label="Percentual do serviço (%)"
                        data-testid="receiving-plan-service-percentage-input"
                        data-validation-field="USD.servicePercentage"
                        error={fieldError('USD', 'servicePercentage')}
                        helperText="Maior que zero, com até três casas decimais."
                        value={draft.serviceFee?.percentage ?? '9.9'}
                        onValueChange={(percentage) =>
                          setDraft((value) => ({
                            ...value,
                            serviceFee: {
                              fixedUsd: value.serviceFee?.fixedUsd ?? '1.49',
                              percentage,
                            },
                          }))
                        }
                      />
                      <CurrencyField
                        label="Parcela fixa do serviço (USD)"
                        data-testid="receiving-plan-service-fixed-input"
                        data-validation-field="USD.serviceFixed"
                        currency="USD"
                        error={fieldError('USD', 'serviceFixed')}
                        helperText="A partir de US$ 0,01."
                        value={draft.serviceFee?.fixedUsd ?? '1.49'}
                        onValueChange={(fixedUsd) =>
                          setDraft((value) => ({
                            ...value,
                            serviceFee: {
                              percentage: value.serviceFee?.percentage ?? '9.9',
                              fixedUsd,
                            },
                          }))
                        }
                      />
                    </div>
                    <div className={styles.header} data-testid="receiving-plan-service-default">
                      <p className={styles.copy}>
                        Padrão do serviço: {planPercent(standardServiceFee.percentage)} +{' '}
                        {new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'USD',
                        }).format(Number(standardServiceFee.fixedUsd))}
                        . A reserva permanece como está.
                      </p>
                      <Button
                        variant="secondary"
                        size="sm"
                        data-testid="receiving-plan-reset-service-btn"
                        disabled={
                          Number(draft.serviceFee?.percentage ?? '9.9') ===
                            Number(standardServiceFee.percentage) &&
                          Number(draft.serviceFee?.fixedUsd ?? '1.49') ===
                            Number(standardServiceFee.fixedUsd)
                        }
                        onClick={() => {
                          setDraft((value) => ({ ...value, serviceFee: standardServiceFee }))
                          setErrors([])
                          setFieldIssues([])
                        }}
                      >
                        Restaurar só a taxa padrão
                      </Button>
                    </div>
                  </DrawerSection>
                ) : null}
                <DrawerSection
                  title="Reserva em reais"
                  description={
                    customFees
                      ? 'A reserva segue a condição de cada participante e depende da confirmação da Stripe.'
                      : 'Serviço VendePay: 9,9% + US$ 1,49 por cobrança, com a parcela fixa convertida em reais. A tarifa é igual para todas as vendas.'
                  }
                >
                  <div className={styles.fields}>
                    <PercentageField
                      preserveInput={customFees}
                      data-testid="receiving-plan-reserve-percentage-input"
                      data-validation-field={fieldKey('BRL', 'nationalReservePercentage')}
                      label="Reserva nacional e Pix (%)"
                      error={fieldError('BRL', 'nationalReservePercentage')}
                      value={national.nationalReservePercentage}
                      onValueChange={(nationalReservePercentage) =>
                        patchFee({ nationalReservePercentage })
                      }
                    />
                    {days(
                      'Liberação da reserva nacional (dias)',
                      national.nationalReserveDays,
                      (nationalReserveDays) => patchFee({ nationalReserveDays }),
                      'BRL',
                      'nationalReserveDays',
                    )}
                  </div>
                </DrawerSection>

                <DrawerSection
                  title="Taxas configuradas no plano"
                  description="Valores preservados para histórico e configuração do plano. Não representam cobrança automática nem tarifa Stripe. A tarifa efetiva só é observada na composição Stripe."
                >
                  <CurrencyField
                    data-validation-field={fieldKey(withdrawalCurrency, 'withdrawalFees')}
                    label={`Taxa configurada (${withdrawalCurrency})`}
                    error={fieldError(withdrawalCurrency, 'withdrawalFees')}
                    currency={withdrawalCurrency}
                    value={draft.withdrawalFees?.BRL ?? draft.withdrawalFeeBrl}
                    onValueChange={(fee) =>
                      setDraft((value) => {
                        const withdrawalFees = { ...value.withdrawalFees }
                        if (fee === '') delete withdrawalFees[withdrawalCurrency]
                        else withdrawalFees[withdrawalCurrency] = fee
                        return {
                          ...value,
                          withdrawalFees,
                          withdrawalFeeBrl: fee,
                        }
                      })
                    }
                  />
                </DrawerSection>
              </>
            ) : (
              <PlanConditions plan={draft} />
            )}
            <TextField
              data-validation-field={fieldKey('', 'reason')}
              label="Motivo da alteração"
              error={fieldError('', 'reason')}
              value={reason}
              maxLength={500}
              onChange={(event) => setReason(event.target.value)}
              helperText={
                scope === 'default'
                  ? 'O motivo e o responsável ficam registrados no histórico do plano padrão.'
                  : 'O motivo e o responsável ficam registrados no histórico do usuário.'
              }
            />
          </>
        )}
      </div>
    </Drawer>
  )
}
